import React from 'react';
import {
  ComposedChart,
  Line,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Loader2, AlertCircle } from 'lucide-react';

const ComposedChartComponent = ({
  data = [],
  series = [
    { type: 'bar', key: 'barVal', name: 'Bar Value', color: '#0052FF' },
    { type: 'line', key: 'lineVal', name: 'Line Value', color: '#10B981', yAxisId: 'right' },
  ],
  xAxisKey = 'name',
  height = 300,
  loading = false,
  error = null,
  title = '',
  formatterLeft = (v) => v,
  formatterRight = (v) => v,
}) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center bg-white border border-slate-200 rounded-xl p-6" style={{ height }}>
        <Loader2 className="w-8 h-8 animate-spin text-[#0052FF] mb-2" />
        <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Loading Chart...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center bg-white border border-slate-200 rounded-xl p-6 text-center" style={{ height }}>
        <AlertCircle className="w-8 h-8 text-amber-500 mb-2" />
        <span className="text-xs text-slate-600 font-medium">{error}</span>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center bg-white border border-slate-200 rounded-xl p-6 text-center" style={{ height }}>
        <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider">No Data Available</span>
      </div>
    );
  }

  const hasRightAxis = series.some((s) => s.yAxisId === 'right');

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-3" data-testid="composed-chart-card">
      {title && <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">{title}</h4>}
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 10, right: hasRightAxis ? 20 : 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
            <XAxis dataKey={xAxisKey} tick={{ fill: '#64748B', fontSize: 11 }} axisLine={{ stroke: '#E2E8F0' }} />
            <YAxis yAxisId="left" tickFormatter={formatterLeft} tick={{ fill: '#64748B', fontSize: 11 }} axisLine={{ stroke: '#E2E8F0' }} />
            {hasRightAxis && (
              <YAxis yAxisId="right" orientation="right" tickFormatter={formatterRight} tick={{ fill: '#64748B', fontSize: 11 }} axisLine={{ stroke: '#E2E8F0' }} />
            )}
            <Tooltip
              contentStyle={{ backgroundColor: '#0F172A', borderColor: '#334155', borderRadius: '8px', color: '#FFF', fontSize: '12px' }}
              formatter={(value, name, item) => [
                item.yAxisId === 'right' ? formatterRight(value) : formatterLeft(value),
                name,
              ]}
            />
            <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
            {series.map((s) => {
              const yId = s.yAxisId || 'left';
              if (s.type === 'bar') {
                return (
                  <Bar
                    key={s.key}
                    yAxisId={yId}
                    dataKey={s.key}
                    name={s.name || s.key}
                    fill={s.color || '#0052FF'}
                    radius={[4, 4, 0, 0]}
                  />
                );
              }
              if (s.type === 'area') {
                return (
                  <Area
                    key={s.key}
                    yAxisId={yId}
                    type="monotone"
                    dataKey={s.key}
                    name={s.name || s.key}
                    fill={s.color || '#6366F1'}
                    stroke={s.color || '#6366F1'}
                    fillOpacity={0.2}
                  />
                );
              }
              return (
                <Line
                  key={s.key}
                  yAxisId={yId}
                  type="monotone"
                  dataKey={s.key}
                  name={s.name || s.key}
                  stroke={s.color || '#10B981'}
                  strokeWidth={2.5}
                  dot={{ r: 4 }}
                />
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ComposedChartComponent;
